/**
 * Staff Bookings Portal
 * =====================
 * 
 * Staff members can view their upcoming appointments.
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CalendarDays,
} from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  confirmed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  in_progress: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  completed: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-slate-500/20 text-gray-500 border-slate-500/30",
  no_show: "bg-red-500/20 text-red-400 border-red-500/30",
};

const StaffBookingsPage = () => {
  const { token, user } = useAuth();
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(0); // 0 = current week, 1 = next week, etc.

  const getDateRange = () => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() + currentWeek * 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    
    return {
      start: start.toISOString().split("T")[0],
      end: end.toISOString().split("T")[0],
    };
  };

  const fetchBookings = async () => {
    const { start, end } = getDateRange();
    
    try {
      const res = await fetch(
        `${API}/bookings/staff-portal/my-bookings?start_date=${start}&end_date=${end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setBookings(data);
      } else if (res.status === 403) {
        toast.error("You don't have access to staff bookings");
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [token, currentWeek]);

  const { start, end } = getDateRange();
  
  // Group bookings by date
  const bookingsByDate = {};
  bookings.forEach((booking) => {
    if (!bookingsByDate[booking.booking_date]) {
      bookingsByDate[booking.booking_date] = [];
    }
    bookingsByDate[booking.booking_date].push(booking);
  });

  // Generate dates for the week
  const weekDates = [];
  const startDate = new Date(start);
  for (let i = 0; i < 7; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    weekDates.push(d.toISOString().split("T")[0]);
  }

  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-dark border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/merchant" className="p-2 rounded-lg hover:bg-white/5">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold">My Schedule</h1>
              <p className="text-sm text-gray-500">{user?.name || "Staff Member"}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Week Navigation */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentWeek((w) => w - 1)}
            className="border-gray-200"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          
          <h2 className="text-lg font-semibold">
            {new Date(start).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} - {new Date(end).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
          </h2>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentWeek((w) => w + 1)}
            className="border-gray-200"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
          </div>
        ) : (
          <div className="space-y-4">
            {weekDates.map((date) => {
              const dateObj = new Date(date);
              const isToday = date === new Date().toISOString().split("T")[0];
              const dayBookings = bookingsByDate[date] || [];
              
              return (
                <div
                  key={date}
                  className={`rounded-xl border ${isToday ? "border-indigo-500/50 bg-indigo-500/5" : "border-gray-200 bg-white/30"}`}
                >
                  <div className={`p-4 border-b ${isToday ? "border-indigo-500/30" : "border-white/5"}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isToday ? "bg-indigo-500/20" : "bg-gray-100"}`}>
                        <span className={`text-lg font-bold ${isToday ? "text-indigo-400" : ""}`}>
                          {dateObj.getDate()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">
                          {dateObj.toLocaleDateString("en-GB", { weekday: "long" })}
                          {isToday && <span className="text-indigo-400 ml-2">(Today)</span>}
                        </p>
                        <p className="text-sm text-gray-500">
                          {dateObj.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                        </p>
                      </div>
                      <Badge className="ml-auto">{dayBookings.length} appointments</Badge>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    {dayBookings.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No appointments</p>
                    ) : (
                      <div className="space-y-3">
                        {dayBookings.sort((a, b) => a.start_time.localeCompare(b.start_time)).map((booking) => (
                          <div
                            key={booking.booking_id}
                            className={`p-4 rounded-lg border ${STATUS_COLORS[booking.status]}`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="font-semibold">{booking.service_name}</p>
                                <div className="flex items-center gap-2 text-sm mt-1">
                                  <Clock className="h-4 w-4" />
                                  <span>{booking.start_time} - {booking.end_time}</span>
                                  <span className="text-gray-500">({booking.duration_minutes}min)</span>
                                </div>
                              </div>
                              <Badge className={STATUS_COLORS[booking.status]}>
                                {booking.status.replace("_", " ")}
                              </Badge>
                            </div>
                            
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center gap-2 mb-1">
                                <User className="h-4 w-4 text-gray-500" />
                                <span className="font-medium">{booking.customer_name}</span>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {booking.customer_email}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {booking.customer_phone}
                                </span>
                              </div>
                            </div>
                            
                            {booking.notes && (
                              <div className="mt-2 p-2 rounded bg-gray-100/50 text-sm text-gray-500">
                                {booking.notes}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffBookingsPage;
